import { useTranslation } from "react-i18next";
import * as React from "react";
import { useOrganization } from "@clerk/clerk-react";
import type { Customer } from "@shared/schema";
import { getCustomerDisplayName } from "@shared/customer-utils";

interface ProfessionalQuoteLayoutProps {
  children: React.ReactNode;
  title: string;
  quoteNumber?: string;
  date?: string;
  status?: string;
  statusColor?: string;
  customer?: Partial<Customer>;
  showCompanyHeader?: boolean;
  className?: string;
  organization?: {
    name?: string;
    imageUrl?: string;
    url?: string;
    address?: {
      streetAddress?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    } | null;
    phone?: string | null;
  };
}

export const ProfessionalQuoteLayout: React.FC<
  ProfessionalQuoteLayoutProps
> = ({
  children,
  title,
  quoteNumber,
  date,
  status,
  statusColor = "bg-blue-100 text-blue-800",
  customer,
  showCompanyHeader = true,
  className = "",
  organization: providedOrganization,
}) => {
  const { t } = useTranslation();
  const { organization: clerkOrganization } = useOrganization();

  // Use provided organization data (for public pages) or fallback to Clerk organization
  const organization = providedOrganization || clerkOrganization;

  // Type guard to check if organization has extended properties (address/phone)
  const hasExtendedOrgData =
    providedOrganization?.address &&
    providedOrganization.address.streetAddress &&
    providedOrganization.address.city &&
    providedOrganization.address.state &&
    providedOrganization.address.zipCode;

  return (
    <div className={`bg-white ${className}`}>
      {/* Company Header */}
      {showCompanyHeader && (
        <div className="border-b-2 border-primary/20 pb-6 mb-8">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-4">
              {organization?.imageUrl && (
                <img
                  src={organization.imageUrl}
                  alt={`${organization.name} logo`}
                  className="w-12 h-12 object-contain rounded ml-2 mt-2"
                />
              )}
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  {organization?.name || t("features.quotes.quoteViewer.viewerLayout.contractorpro")}
                </h1>
                {/* Company Address */}
                {hasExtendedOrgData && providedOrganization.address && (
                  <div className="text-sm text-slate-600 mt-1 max-w-xs">
                    <div className="mt-1">
                      {providedOrganization.address.streetAddress && (
                        <div className="break-words">
                          {providedOrganization.address.streetAddress}
                        </div>
                      )}
                      {(providedOrganization.address.city ||
                        providedOrganization.address.state ||
                        providedOrganization.address.zipCode) && (
                        <div className="break-words">
                          {providedOrganization.address.city &&
                            `${providedOrganization.address.city}, `}
                          {providedOrganization.address.state &&
                            `${providedOrganization.address.state} `}
                          {providedOrganization.address.zipCode}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="text-right">
              {date && (
                <div className="text-sm text-slate-600">
                  <span className="font-medium">{t("features.quotes.quoteViewer.viewerLayout.created")}</span> {date}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quote Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-3xl font-bold text-slate-900">{title}</h2>
            {quoteNumber && (
              <div className="text-sm text-slate-600 mt-1">
                <span className="font-medium">{t("features.quotes.quoteViewer.viewerLayout.quote")}</span> {quoteNumber}
              </div>
            )}
          </div>
          {status && (
            <div
              className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}
            >
              {status}
            </div>
          )}
        </div>

        {/* Customer Information */}
        {customer && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-4">
            <h3 className="font-semibold text-slate-900 mb-2">{t("features.quotes.quoteViewer.viewerLayout.billTo")}</h3>
            <div className="text-slate-700">
              <p className="font-medium">{getCustomerDisplayName(customer)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="space-y-8">{children}</div>
    </div>
  );
};
